import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const LANGUAGE_STORAGE_KEY = "zedro_academy_language";

function getLanguage() {
  if (typeof window === "undefined") return "es";
  return window.localStorage.getItem(LANGUAGE_STORAGE_KEY) === "fr" ? "fr" : "es";
}

const COPY = {
  es: {
    eyebrow: "ZEDRO Academy",
    title: "Tienda",
    intro:
      "Explora los programas, herramientas y recursos de ZEDRO Academy. Cada producto conserva su propia propuesta, condiciones y experiencia de acceso.",
    sectionTitle: "Programas",
    founderBadge: "Edición Fundadora",
    status: "Próxima apertura",
    productTitle: "Mente en Dirección",
    productText:
      "Una experiencia práctica de Dirección Personal para convertir claridad en decisiones, decisiones en acciones y acciones en evidencia de avance sobre un objetivo real.",
    cta: "Ver Edición Fundadora",
    note: "La Edición Fundadora aún no está abierta a compra.",
  },
  fr: {
    eyebrow: "ZEDRO Academy",
    title: "Boutique",
    intro:
      "Découvrez les programmes, outils et ressources de ZEDRO Academy. Chaque produit conserve sa propre proposition, ses conditions et son expérience d’accès.",
    sectionTitle: "Programmes",
    founderBadge: "Édition fondatrice",
    status: "Ouverture prochaine",
    productTitle: "Clarté en action",
    productText:
      "Une expérience pratique de Direction personnelle pour transformer la clarté en décisions, les décisions en actions et les actions en preuves de progrès sur un objectif réel.",
    cta: "Voir l’Édition fondatrice",
    note: "L’Édition fondatrice n’est pas encore ouverte à l’achat.",
  },
};

export default function StorePublicLanding() {
  const [language, setLanguage] = useState(getLanguage);
  const copy = COPY[language] || COPY.es;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    function syncLanguage(event) {
      const nextLanguage = event?.detail?.language;
      if (nextLanguage === "es" || nextLanguage === "fr") {
        setLanguage(nextLanguage);
      }
    }

    window.addEventListener("zedro:languagechange", syncLanguage);
    return () => window.removeEventListener("zedro:languagechange", syncLanguage);
  }, []);

  return (
    <main style={styles.page}>
      <div style={styles.shell}>
        <header style={styles.header}>
          <div style={styles.eyebrow}>{copy.eyebrow}</div>
          <h1 style={styles.title}>{copy.title}</h1>
          <p style={styles.intro}>{copy.intro}</p>
        </header>

        <section aria-labelledby="store-programs-title">
          <div style={styles.sectionHeader}>
            <h2 id="store-programs-title" style={styles.sectionTitle}>{copy.sectionTitle}</h2>
          </div>

          <div style={styles.catalogGrid}>
            <article style={styles.productCard}>
              <div style={styles.thumbWrap}>
                <img
                  src="/assets/images/home/home-mente-direccion-editorial-v1.webp"
                  alt={copy.productTitle}
                  style={styles.thumb}
                />
                <span style={styles.statusBadge}>{copy.status}</span>
              </div>

              <div style={styles.cardBody}>
                <div style={styles.founderBadge}>{copy.founderBadge}</div>
                <h3 style={styles.productTitle}>{copy.productTitle}</h3>
                <p style={styles.productText}>{copy.productText}</p>
                <p style={styles.note}>{copy.note}</p>
                <Link
                  to={language === "fr" ? "/fr/edition-fondatrice" : "/edicion-fundadora"}
                  style={styles.button}
                >
                  {copy.cta}
                </Link>
              </div>
            </article>
          </div>
        </section>
      </div>
    </main>
  );
}

const styles = {
  page: {
    minHeight: "70vh",
    background: "#f5f7f9",
    padding: "36px 20px 64px",
    color: "#264763",
  },
  shell: {
    maxWidth: 1180,
    margin: "0 auto",
  },
  header: {
    marginBottom: 30,
    maxWidth: 760,
  },
  eyebrow: {
    color: "#CCA352",
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: ".12em",
  },
  title: {
    margin: "6px 0 10px",
    fontFamily: "Bitter, serif",
    fontSize: "clamp(2.2rem,5vw,3.8rem)",
    lineHeight: 1.05,
  },
  intro: {
    margin: 0,
    maxWidth: 720,
    color: "#526b7f",
    lineHeight: 1.7,
    fontSize: 17,
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 14,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 18,
    letterSpacing: ".02em",
  },
  catalogGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 300px))",
    gap: 20,
    alignItems: "start",
  },
  productCard: {
    background: "#fff",
    border: "1px solid rgba(38,71,99,.12)",
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 10px 28px rgba(38,71,99,.06)",
  },
  thumbWrap: {
    position: "relative",
    aspectRatio: "4 / 3",
    overflow: "hidden",
    background: "#e9eef2",
  },
  thumb: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  statusBadge: {
    position: "absolute",
    top: 12,
    right: 12,
    background: "rgba(255,255,255,.94)",
    color: "#264763",
    borderRadius: 999,
    padding: "7px 10px",
    fontSize: 11,
    fontWeight: 800,
    boxShadow: "0 4px 12px rgba(38,71,99,.12)",
  },
  cardBody: {
    padding: 18,
  },
  founderBadge: {
    color: "#CCA352",
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: ".1em",
    marginBottom: 6,
  },
  productTitle: {
    margin: "0 0 10px",
    fontFamily: "Bitter, serif",
    fontSize: 25,
    lineHeight: 1.1,
  },
  productText: {
    margin: 0,
    color: "#526b7f",
    lineHeight: 1.6,
    fontSize: 14,
  },
  note: {
    margin: "12px 0 16px",
    color: "#738493",
    fontSize: 13,
    fontWeight: 700,
  },
  button: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    boxSizing: "border-box",
    background: "#264763",
    color: "#fff",
    textDecoration: "none",
    padding: "11px 14px",
    borderRadius: 999,
    fontWeight: 800,
    fontSize: 14,
  },
};
